import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtBRL, fmtDate } from "./format";
import type { NotaSimplificada } from "./fiscal-types";

interface ExportOptions {
  title: string;
  filename: string;
  companyName?: string;
  cnpj?: string;
  periodo?: { inicio: string; fim: string } | null;
}

export function exportNotesToPDF(
  notas: NotaSimplificada[], 
  options: ExportOptions, 
  columns: { header: string; dataKey: string }[],
  mapper: (n: NotaSimplificada) => any
) {
  const doc = new jsPDF();
  const { title, filename, companyName, cnpj, periodo } = options;

  // Header
  doc.setFontSize(20);
  doc.setTextColor(40);
  doc.text(title, 14, 22);

  doc.setFontSize(10);
  doc.setTextColor(100);
  if (companyName) doc.text(`Empresa: ${companyName}`, 14, 30);
  if (cnpj) doc.text(`CNPJ: ${cnpj}`, 14, 35);
  
  if (periodo) {
    doc.text(`Período: ${fmtDate(periodo.inicio)} até ${fmtDate(periodo.fim)}`, 14, 40);
  }

  doc.text(`Total de Registros: ${notas.length}`, 14, 45);
  doc.text(`Data de Emissão do Relatório: ${new Date().toLocaleString()}`, 14, 50);

  const tableData = notas.map(mapper);

  autoTable(doc, {
    startY: 60,
    head: [columns.map(c => c.header)],
    body: tableData.map(row => columns.map(c => row[c.dataKey])),
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 10, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 60 },
  });

  // Totais no final
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  const totalValue = notas.reduce((acc, n) => acc + (n.valorOficial || 0), 0);
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(`Valor Total Consolidado: ${fmtBRL(totalValue)}`, 14, finalY);

  doc.save(`${filename}_${new Date().getTime()}.pdf`);
}

export function exportFinanceiroToPDF(
  duplicatas: any[], 
  options: ExportOptions
) {
  const doc = new jsPDF();
  const { title, filename, companyName, cnpj, periodo } = options;

  doc.setFontSize(20);
  doc.text(title, 14, 22);

  doc.setFontSize(10);
  doc.setTextColor(100);
  if (companyName) doc.text(`Empresa: ${companyName}`, 14, 30);
  if (cnpj) doc.text(`CNPJ: ${cnpj}`, 14, 35);
  if (periodo) doc.text(`Período: ${fmtDate(periodo.inicio)} até ${fmtDate(periodo.fim)}`, 14, 40);
  
  doc.text(`Total de Duplicatas: ${duplicatas.length}`, 14, 45);

  const tableData = duplicatas.map(d => [
    fmtDate(d.dVenc),
    d.entidade,
    d.tipo,
    d.nDup,
    fmtBRL(d.vDup)
  ]);

  autoTable(doc, {
    startY: 55,
    head: [["Vencimento", "Entidade", "Tipo", "Documento", "Valor"]],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [243, 156, 18], textColor: 255 },
    styles: { fontSize: 8 },
  });

  const finalY = (doc as any).lastAutoTable.finalY + 10;
  const pagar = duplicatas.filter(d => d.tipo === "PAGAR").reduce((a, b) => a + b.vDup, 0);
  const receber = duplicatas.filter(d => d.tipo === "RECEBER").reduce((a, b) => a + b.vDup, 0);

  doc.text(`Total a Pagar: ${fmtBRL(pagar)}`, 14, finalY);
  doc.text(`Total a Receber: ${fmtBRL(receber)}`, 14, finalY + 7);
  doc.text(`Saldo Líquido: ${fmtBRL(receber - pagar)}`, 14, finalY + 14);

  doc.save(`${filename}_${new Date().getTime()}.pdf`);
}

export function exportDashboardPdf(dashboard: any, notas: any[], duration: number) {
  const doc = new jsPDF();
  doc.setFontSize(22);
  doc.text("Executive Intelligence Report", 14, 22);
  
  doc.setFontSize(10);
  doc.text(`Empresa: ${dashboard.nomeEmpresa || "N/A"}`, 14, 30);
  doc.text(`CNPJ: ${dashboard.cnpjEmpresa || "N/A"}`, 14, 35);
  doc.text(`Data: ${new Date().toLocaleString()}`, 14, 40);
  doc.text(`Notas Processadas: ${notas.length}`, 14, 45);
  doc.text(`Tempo de Processamento: ${duration}ms`, 14, 50);

  autoTable(doc, {
    startY: 60,
    head: [["Métrica", "Valor"]],
    body: [
      ["Receita Total", fmtBRL(dashboard.stats.totalRevenue)],
      ["Entradas (Compras)", fmtBRL(dashboard.operacoesSummary?.entradas?.valorTotal || 0)],
      ["Saídas (Vendas)", fmtBRL(dashboard.operacoesSummary?.saidas?.valorTotal || 0)],
      ["Divergências Altas", dashboard.riskSummary.alta.toString()],
      ["Divergências Médias", dashboard.riskSummary.media.toString()],
      ["Score de Saneamento", `${dashboard.saneamento.score.toFixed(2)}%`]
    ],
  });

  doc.save(`dashboard_report_${new Date().getTime()}.pdf`);
}
